import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect, useReducer, useRef } from 'react';

const DrawingLayer = ({ selectedGeometry, savedGeometries = [], onGeometryComplete, onGeometryUpdate, onGeometrySelect }) => {
    const map = useMap();
    const drawing = useMapsLibrary('drawing');
    const overlaysShouldUpdateRef = useRef(true);
    const overlaysRef = useRef([]); // Store all overlays
    const labelsRef = useRef([]); // Store all labels
    const selectedOverlayRef = useRef(null); // Track currently selected overlay
    let drawingManager = null;

    const DrawingActionKind = {
        SET_OVERLAY: 'SET_OVERLAY',
        UPDATE_OVERLAYS: 'UPDATE_OVERLAYS'
    };

    const drawingReducer = (state, action) => {
        switch (action.type) {
            case DrawingActionKind.SET_OVERLAY:
                return { ...state, overlay: action.payload };
            case DrawingActionKind.UPDATE_OVERLAYS:
                return { ...state, lastUpdate: Date.now() };
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(drawingReducer, {});

    // Move addUpdateListener function to component level so it's accessible everywhere
    const addUpdateListener = (eventName, overlayItem) => {
        const updateListener = google.maps.event.addListener(
            overlayItem.overlay,
            eventName,
            () => {
                console.log(`Overlay ${overlayItem.id} ${eventName} event triggered`);
                if (eventName === 'dragstart') {
                    overlaysShouldUpdateRef.current = false;
                }

                if (eventName === 'dragend') {
                    overlaysShouldUpdateRef.current = true;

                    // Update label position when overlay is moved
                    const labelItem = labelsRef.current.find(item => item.geometryId === overlayItem.id);
                    if (labelItem && overlayItem.label) {
                        const newCenter = getOverlayCenter(overlayItem.overlay, overlayItem.type);
                        if (newCenter) {
                            labelItem.label.updatePosition(newCenter);
                        }
                    }
                }

                if (overlaysShouldUpdateRef.current) {
                    dispatch({ type: DrawingActionKind.UPDATE_OVERLAYS });

                    // Notify parent component of geometry update
                    if (onGeometryUpdate) {
                        const geometryData = getGeometryData(overlayItem.overlay, overlayItem.type);
                        onGeometryUpdate({
                            id: overlayItem.id,
                            geometry: geometryData,
                            label: overlayItem.label,
                            color: overlayItem.color
                        });
                    }
                }
            }
        );

        return updateListener; // Return the listener so it can be tracked
    };

    // Function to create/update a label for an overlay
    const createLabel = (overlay, type, labelText, color, geometryId) => {
        const center = getOverlayCenter(overlay, type);
        if (!center || !labelText) return null;

        // Create the BadgeOverlay class using the available google maps object
        const BadgeOverlay = createBadgeOverlayClass(window.google.maps);

        const badgeOverlay = new BadgeOverlay(
            center,
            labelText,
            color,
            geometryId,
            selectOverlay
        );

        badgeOverlay.setMap(map);
        return badgeOverlay;
    };

    const createBadgeOverlayClass = (maps) => {
        return class BadgeOverlay extends maps.OverlayView {
            constructor(position, content, color, geometryId, onBadgeClick) {
                super();
                this.position = position;
                this.content = content;
                this.color = color;
                this.geometryId = geometryId;
                this.onBadgeClick = onBadgeClick;
                this.div = null;
            }

            onAdd() {
                const div = document.createElement('div');
                div.style.cssText = `
                position: absolute;
                color: ${this.getContrastColor(this.color)};
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: bold;
                cursor: pointer;
                user-select: none;
                white-space: nowrap;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                transform: translate(-50%, -100%);
                z-index: 1000;
                background: ${this.color};
            `;

                div.innerHTML = this.content;
                div.dataset.geometryId = this.geometryId;

                div.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (this.onBadgeClick) {
                        this.onBadgeClick(this.geometryId);
                    }
                });

                div.addEventListener('mouseenter', () => {
                    div.style.transform = 'translate(-50%, -100%) scale(1.1)';
                    div.style.transition = 'transform 0.2s ease';
                });

                div.addEventListener('mouseleave', () => {
                    div.style.transform = 'translate(-50%, -100%) scale(1)';
                });

                this.div = div;
                const panes = this.getPanes();
                panes.overlayMouseTarget.appendChild(div);
            }

            draw() {
                if (!this.div) return;

                const overlayProjection = this.getProjection();
                const position = overlayProjection.fromLatLngToDivPixel(this.position);

                if (position) {
                    this.div.style.left = position.x + 'px';
                    this.div.style.top = position.y + 'px';
                }
            }

            onRemove() {
                if (this.div) {
                    this.div.parentNode.removeChild(this.div);
                    this.div = null;
                }
            }

            updateContent(content, color) {
                if (this.div) {
                    this.div.innerHTML = content;
                    this.div.style.color = this.getContrastColor(color);
                }
                this.content = content;
                this.color = color;
            }

            updatePosition(position) {
                this.position = position;
                this.draw();
            }

            getContrastColor(hexColor) {
                if (!hexColor) return '#000000';

                const hex = hexColor.replace('#', '');
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);

                const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                return luminance > 0.5 ? '#000000' : '#ffffff';
            }
        };
    };

    // Function to get center position for different overlay types
    const getOverlayCenter = (overlay, type) => {
        switch (type) {
            case google.maps.drawing.OverlayType.CIRCLE:
                return overlay.getCenter();
            case google.maps.drawing.OverlayType.RECTANGLE:
                const bounds = overlay.getBounds();
                return bounds.getCenter();
            case google.maps.drawing.OverlayType.POLYGON:
                const path = overlay.getPath();
                const vertices = path.getArray();
                let lat = 0, lng = 0;
                vertices.forEach(vertex => {
                    lat += vertex.lat();
                    lng += vertex.lng();
                });
                return new google.maps.LatLng(lat / vertices.length, lng / vertices.length);
            default:
                return null;
        }
    };

    // Function to apply colors to overlay
    const applyOverlayStyle = (overlay, color, isSelected = false) => {
        const fillColor = color || '#000000';
        const strokeColor = color || '#000000';

        overlay.setOptions({
            fillColor: fillColor,
            fillOpacity: isSelected ? 0.65 : 0.3,
            strokeColor: strokeColor,
            strokeWeight: isSelected ? 3 : 2,
            strokeOpacity: isSelected ? 1 : 0.8
        });
    };

    // Function to get geometry data from overlay
    const getGeometryData = (overlay, type) => {
        switch (type) {
            case google.maps.drawing.OverlayType.CIRCLE:
                return {
                    type: 'circle',
                    center: {
                        lat: overlay.getCenter().lat(),
                        lng: overlay.getCenter().lng()
                    },
                    radius: overlay.getRadius()
                };
            case google.maps.drawing.OverlayType.RECTANGLE:
                const bounds = overlay.getBounds();
                return {
                    type: 'rectangle',
                    bounds: {
                        north: bounds.getNorthEast().lat(),
                        south: bounds.getSouthWest().lat(),
                        east: bounds.getNorthEast().lng(),
                        west: bounds.getSouthWest().lng()
                    }
                };
            case google.maps.drawing.OverlayType.POLYGON:
                const path = overlay.getPath();
                return {
                    type: 'polygon',
                    coordinates: path.getArray().map(vertex => ({
                        lat: vertex.lat(),
                        lng: vertex.lng()
                    }))
                };
            default:
                return null;
        }
    };

    // Function to deselect all overlays
    const deselectAllOverlays = () => {
        overlaysRef.current.forEach(item => {
            applyOverlayStyle(item.overlay, item.color, false);
            item.overlay.setOptions({ draggable: false });
        });
        selectedOverlayRef.current = null;
        
        if (onGeometrySelect) {
            onGeometrySelect(null); // Notify parent that nothing is selected
        }
    };

    // Function to select an overlay
    const selectOverlay = (geometryId) => {
        // Reset all overlays to unselected state and make them non-draggable
        overlaysRef.current.forEach(item => {
            applyOverlayStyle(item.overlay, item.color, false);
            item.overlay.setOptions({ draggable: false });
        });

        // Find and select the clicked overlay
        const selectedItem = overlaysRef.current.find(item => item.id === geometryId);
        if (selectedItem) {
            applyOverlayStyle(selectedItem.overlay, selectedItem.color, true);
            selectedItem.overlay.setOptions({ draggable: true }); // Only selected overlay is draggable
            selectedOverlayRef.current = selectedItem;

            if (onGeometrySelect) {
                onGeometrySelect({
                    id: selectedItem.id,
                    label: selectedItem.label,
                    color: selectedItem.color,
                    geometry: getGeometryData(selectedItem.overlay, selectedItem.type)
                });
            }
        }
    };

    // Function to update existing overlay
    const updateExistingOverlay = (geometryId, newLabel, newColor) => {
        const overlayItem = overlaysRef.current.find(item => item.id === geometryId);
        if (!overlayItem) return;

        // Update overlay color
        overlayItem.color = newColor;
        applyOverlayStyle(overlayItem.overlay, newColor, overlayItem.id === selectedOverlayRef.current?.id);

        // Update label
        overlayItem.label = newLabel;

        // Find the label and its index
        const labelIndex = labelsRef.current.findIndex(item => item.geometryId === geometryId);
        const labelItem = labelsRef.current[labelIndex];
        
        if (labelItem) {
            if (newLabel) {
                // Update existing badge
                labelItem.label.updateContent(newLabel, newColor);
            } else {
                // Remove badge if no label - now using the correct labelIndex
                labelItem.label.setMap(null);
                labelsRef.current.splice(labelIndex, 1);
            }
        } else if (newLabel) {
            // Create new badge if one doesn't exist
            const newBadge = createLabel(overlayItem.overlay, overlayItem.type, newLabel, newColor, geometryId);
            if (newBadge) {
                labelsRef.current.push({
                    label: newBadge,
                    geometryId: geometryId
                });
            }
        }
    };

    // Load saved geometries when component mounts - only run once on initial load
    const initialLoadRef = useRef(true);

    useEffect(() => {
        if (!map || !drawing) return;

        // Only run this on the initial load, not on every savedGeometries change
        if (!initialLoadRef.current || savedGeometries.length === 0) return;

        initialLoadRef.current = false;

        // Clear existing overlays
        overlaysRef.current.forEach(item => {
            if (item.overlay) {
                item.overlay.setMap(null);
            }
        });
        overlaysRef.current = [];

        labelsRef.current.forEach(item => {
            if (item.label) {
                item.label.setMap(null);
            }
        });
        labelsRef.current = [];

        // Recreate overlays from saved data
        savedGeometries.forEach(savedGeometry => {
            let overlay = null;

            switch (savedGeometry.geometry.type) {
                case 'circle':
                    overlay = new google.maps.Circle({
                        center: savedGeometry.geometry.center,
                        radius: savedGeometry.geometry.radius,
                        map: map,
                        editable: true,
                        draggable: false // Start as non-draggable
                    });
                    break;
                case 'rectangle':
                    overlay = new google.maps.Rectangle({
                        bounds: savedGeometry.geometry.bounds,
                        map: map,
                        editable: true,
                        draggable: false // Start as non-draggable
                    });
                    break;
                case 'polygon':
                    overlay = new google.maps.Polygon({
                        paths: savedGeometry.geometry.coordinates,
                        map: map,
                        editable: true,
                        draggable: false // Start as non-draggable
                    });
                    break;
            }

            if (overlay) {
                // Apply styling
                applyOverlayStyle(overlay, savedGeometry.color);

                // Store overlay
                const overlayItem = {
                    id: savedGeometry.id,
                    overlay: overlay,
                    type: google.maps.drawing.OverlayType[savedGeometry.geometry.type.toUpperCase()],
                    label: savedGeometry.label,
                    color: savedGeometry.color
                };
                overlaysRef.current.push(overlayItem);

                // Create label
                if (savedGeometry.label) {
                    const label = createLabel(overlay, overlayItem.type, savedGeometry.label, savedGeometry.color, savedGeometry.id);
                    if (label) {
                        labelsRef.current.push({
                            label: label,
                            geometryId: savedGeometry.id
                        });
                    }
                }

                // Add click listener to select overlay
                google.maps.event.addListener(overlay, 'click', () => {
                    selectOverlay(savedGeometry.id);
                });

                // Add update event listeners for saved geometries
                switch (savedGeometry.geometry.type) {
                    case 'circle':
                        ['center_changed', 'radius_changed', 'dragend'].forEach(eventName =>
                            addUpdateListener(eventName, overlayItem)
                        );
                        break;
                    case 'polygon':
                        ['dragend'].forEach(eventName =>
                            addUpdateListener(eventName, overlayItem)
                        );
                        break;
                    case 'rectangle':
                        ['dragend'].forEach(eventName =>
                            addUpdateListener(eventName, overlayItem)
                        );
                        break;
                }
            }
        });
    }, [map, drawing]); // Removed savedGeometries dependency

    // Handle adding new saved geometries and removing deleted ones
    const prevSavedGeometriesRef = useRef([]);
    useEffect(() => {
        if (!map || !drawing) return;

        // Find newly added geometries
        const newGeometries = savedGeometries.filter(saved =>
            !prevSavedGeometriesRef.current.some(prev => prev.id === saved.id)
        );

        // Find deleted geometries
        const deletedGeometries = prevSavedGeometriesRef.current.filter(prev =>
            !savedGeometries.some(saved => saved.id === prev.id)
        );

        // Remove deleted geometries from the map
        deletedGeometries.forEach(deletedGeometry => {
            // Find and remove the overlay
            const overlayIndex = overlaysRef.current.findIndex(item => item.id === deletedGeometry.id);
            if (overlayIndex !== -1) {
                const overlayItem = overlaysRef.current[overlayIndex];
                overlayItem.overlay.setMap(null);
                overlaysRef.current.splice(overlayIndex, 1);

                // If this was the selected overlay, clear the selection
                if (selectedOverlayRef.current?.id === deletedGeometry.id) {
                    selectedOverlayRef.current = null;
                }
            }

            // Find and remove the label
            const labelIndex = labelsRef.current.findIndex(item => item.geometryId === deletedGeometry.id);
            if (labelIndex !== -1) {
                const labelItem = labelsRef.current[labelIndex];
                labelItem.label.setMap(null);
                labelsRef.current.splice(labelIndex, 1);
            }
        });

        // Add only the new geometries to the map
        newGeometries.forEach(savedGeometry => {
            // Skip if this geometry is already on the map
            if (overlaysRef.current.some(item => item.id === savedGeometry.id)) return;

            let overlay = null;

            switch (savedGeometry.geometry.type) {
                case 'circle':
                    overlay = new google.maps.Circle({
                        center: savedGeometry.geometry.center,
                        radius: savedGeometry.geometry.radius,
                        map: map,
                        editable: true,
                        draggable: false 
                    });
                    break;
                case 'rectangle':
                    overlay = new google.maps.Rectangle({
                        bounds: savedGeometry.geometry.bounds,
                        map: map,
                        editable: true,
                        draggable: false
                    });
                    break;
                case 'polygon':
                    overlay = new google.maps.Polygon({
                        paths: savedGeometry.geometry.coordinates,
                        map: map,
                        editable: true,
                        draggable: false
                    });
                    break;
            }

            if (overlay) {
                // Apply styling
                applyOverlayStyle(overlay, savedGeometry.color);

                // Store overlay
                const overlayItem = {
                    id: savedGeometry.id,
                    overlay: overlay,
                    type: google.maps.drawing.OverlayType[savedGeometry.geometry.type.toUpperCase()],
                    label: savedGeometry.label,
                    color: savedGeometry.color
                };
                overlaysRef.current.push(overlayItem);

                // Create label
                if (savedGeometry.label) {
                    const label = createLabel(overlay, overlayItem.type, savedGeometry.label, savedGeometry.color, savedGeometry.id);
                    if (label) {
                        labelsRef.current.push({
                            label: label,
                            geometryId: savedGeometry.id
                        });
                    }
                }

                // Add click listener to select overlay
                google.maps.event.addListener(overlay, 'click', () => {
                    selectOverlay(savedGeometry.id);
                });

                // Add update event listeners for new saved geometries
                switch (savedGeometry.geometry.type) {
                    case 'circle':
                        ['center_changed', 'radius_changed', 'dragend'].forEach(eventName =>
                            addUpdateListener(eventName, overlayItem)
                        );
                        break;
                    case 'polygon':
                        ['dragend'].forEach(eventName =>
                            addUpdateListener(eventName, overlayItem)
                        );
                        break;
                    case 'rectangle':
                        ['dragend'].forEach(eventName =>
                            addUpdateListener(eventName, overlayItem)
                        );
                        break;
                }
            }
        });

        // Update the reference for next comparison
        prevSavedGeometriesRef.current = [...savedGeometries];
    }, [savedGeometries, map, drawing]);

    // Update selected overlay when selectedGeometry changes
    useEffect(() => {
        if (selectedGeometry && selectedGeometry.id && selectedOverlayRef.current?.id === selectedGeometry.id) {
            updateExistingOverlay(selectedGeometry.id, selectedGeometry.label, selectedGeometry.color);

            onGeometryUpdate({
                id: selectedGeometry.id,
                geometry: selectedGeometry.geometry,
                label: selectedGeometry.label,
                color: selectedGeometry.color
            });
        }
    }, [selectedGeometry?.color, selectedGeometry?.label]);

    useEffect(() => {
        if (!map || !drawing) return;

        const eventListeners = [];

        // https://developers.google.com/maps/documentation/javascript/reference/drawing
        const newDrawingManager = new drawing.DrawingManager({
            map,
            drawingMode: null,
            drawingControl: true,
            drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_LEFT,
                drawingModes: [
                    google.maps.drawing.OverlayType.CIRCLE,
                    google.maps.drawing.OverlayType.RECTANGLE,
                    google.maps.drawing.OverlayType.POLYGON
                ]
            },
            circleOptions: {
                editable: true,
                draggable: true,
                fillColor: '#FF6B6B',
                fillOpacity: 0.3,
                strokeColor: '#D63031',
                strokeWeight: 2,
                strokeOpacity: 0.8
            },
            polygonOptions: {
                editable: true,
                draggable: true,
                fillColor: '#74B9FF',
                fillOpacity: 0.3,
                strokeColor: '#0984E3',
                strokeWeight: 2,
                strokeOpacity: 0.8
            },
            rectangleOptions: {
                editable: true,
                draggable: true,
                fillColor: '#A29BFE',
                fillOpacity: 0.3,
                strokeColor: '#6C5CE7',
                strokeWeight: 2,
                strokeOpacity: 0.8
            },
        });

        drawingManager = newDrawingManager;

        // Add click listener to map to deselect overlays when clicking empty space
        const mapClickListener = google.maps.event.addListener(map, 'click', (event) => {
            // Only deselect if not clicking on an overlay
            deselectAllOverlays();
        });
        eventListeners.push(mapClickListener);

        const overlayCompleteListener = google.maps.event.addListener(newDrawingManager, 'overlaycomplete', (drawResult) => {
            const geometryId = `geometry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const defaultColor = '#000000';

            // Apply styling to the newly created overlay
            applyOverlayStyle(drawResult.overlay, defaultColor);

            // Store overlay reference
            const overlayItem = {
                id: geometryId,
                overlay: drawResult.overlay,
                type: drawResult.type,
                label: '',
                color: defaultColor
            };
            overlaysRef.current.push(overlayItem);

            // Add click listener to select overlay
            google.maps.event.addListener(drawResult.overlay, 'click', () => {
                selectOverlay(geometryId);
            });

            // Add event listeners for updates - now properly accessible
            switch (drawResult.type) {
                case google.maps.drawing.OverlayType.CIRCLE:
                    ['center_changed', 'radius_changed', 'dragend'].forEach(eventName => {
                        const listener = addUpdateListener(eventName, overlayItem);
                        eventListeners.push(listener);
                    });
                    break;

                case google.maps.drawing.OverlayType.POLYGON:
                case google.maps.drawing.OverlayType.POLYLINE:
                    ['dragend'].forEach(eventName => {
                        const listener = addUpdateListener(eventName, overlayItem);
                        eventListeners.push(listener);
                    });
                    break;

                case google.maps.drawing.OverlayType.RECTANGLE:
                    ['dragend'].forEach(eventName => {
                        const listener = addUpdateListener(eventName, overlayItem);
                        eventListeners.push(listener);
                    });
                    break;
            }

            dispatch({ type: DrawingActionKind.SET_OVERLAY, payload: drawResult });

            // Select the newly created overlay
            selectOverlay(geometryId);

            // Notify parent component of new geometry
            if (onGeometryComplete) {
                const geometryData = getGeometryData(drawResult.overlay, drawResult.type);
                // set current mode to null (drawing)
                newDrawingManager.setDrawingMode(null);

                onGeometryComplete({
                    id: geometryId,
                    geometry: geometryData,
                    label: '',
                    color: defaultColor
                });
            }
        });

        eventListeners.push(overlayCompleteListener);

        return () => {
            // Clean up event listeners
            eventListeners.forEach(element => {
                google.maps.event.removeListener(element);
            });

            newDrawingManager.setMap(null);
        };
    }, [drawing, map]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            overlaysRef.current.forEach(item => {
                if (item.overlay) {
                    item.overlay.setMap(null);
                }
            });
            overlaysRef.current = [];

            labelsRef.current.forEach(item => {
                if (item.label) {
                    item.label.setMap(null);
                }
            });
            labelsRef.current = [];
        };
    }, []);

    return null;
};

export default DrawingLayer;